<template>
  <PModal
    title="Corbeille"
    :model-value="modelValue"
    @update:model-value="emit('update:modelValue', $event)"
    @close="onClose"
    size="lg"
  >
    <template #default="{ titleId }">
      <div>
        <div :aria-labelledby="titleId" class="flex flex-col gap-1">
          <Document v-for="item in paginatedDocs" :key="item.id" :doc="item" readonly>
            <template #actions>
              <PTooltip :label="`supprimé le ${formatDate(item.deletedAt!)}`" :offset="5" :delay="500">
                <span class="mr-1v">{{ daysLeft(item) }}</span>
              </PTooltip>
              <PTooltip :label="getRestoreTooltipMsg(item)" :offset="5" :delay="500">
                <PButton
                  size="icon"
                  mode="tertiary-borderless"
                  aria-label="Restaurer le document"
                  :data-sentry-id="`RestoreDoc-${item.id}`"
                  :disabled="letterFile?.archived"
                  @click="restore(item)"
                >
                  <OhVueIcon scale="1.5" :name="restoreIcon" />
                </PButton>
              </PTooltip>
              <!-- <PTooltip
                label="Supprimer définitivement le document"
                :offset="5"
                :delay="500"
              >
                <PButton
                  size="icon"
                  mode="tertiary-borderless"
                  aria-label="Supprimer définitivement le document"
                  :data-sentry-id="`PermaDeleteDoc-${item.id}`"
                  color="error"
                  @click="permaDelete(item)"
                >
                  <OhVueIcon scale="1.5" :name="deleteIcon" />
                </PButton>
              </PTooltip> -->
            </template>
          </Document>
        </div>
        <PPagination
          v-if="documents.length > 5"
          :current-page="page"
          :total="total"
          :page-size="pageSize"
          @change="onPaginationChange"
          size-selector-position="hidden"
          class="mt-1v"
        />
        <p class="italic text-grey-200 mt-2">
          Les documents placés dans la corbeille depuis plus
          {{ replacePlural(docExpiry, "d'un jour", `de ${docExpiry} jours`) }}
          sont automatiquement et définitivement supprimés, ainsi que leurs versions antérieures, sans aucune
          possibilité de restauration
        </p>
      </div>
    </template>
  </PModal>
</template>

<script setup lang="ts">
import {
  AlertDuration,
  AlertType,
  PButton,
  PModal,
  PPagination,
  PTooltip,
  useAlertManager,
  useIcons,
} from "@pasta/box-v2";
import dayjs from "dayjs";
import { OhVueIcon } from "oh-vue-icons";
import { LaTrashRestoreAltSolid } from "oh-vue-icons/icons";
import { computed, inject, ref } from "vue";
import { useNotifyAsyncOperation } from "../composable/useAsyncOperation";
import { type LetterFileDocumentDto, LetterFileDocumentType } from "../dto/letter-file-document.dto";
import { useLetterFileDocumentApi } from "../http/letter-file-document.api";
import { useSimpleFeatureStore } from "../stores/simple-features.store";
import { FormOperation } from "../types";
import { replacePlural } from "../utils/plural";
import Document from "../views/LetterFileForm/Documents/Document.vue";
import { type FormProvide, FormToken } from "../views/LetterFileForm/utils";

const [restoreIcon] = useIcons(LaTrashRestoreAltSolid);

interface Props {
  modelValue: boolean;
  documents: LetterFileDocumentDto[];
}

const props = defineProps<Props>();
const emit = defineEmits<{ (e: "update:modelValue", value: boolean): void }>();

const notifyAsyncOperation = useNotifyAsyncOperation();
const { refresh, letterFile } = inject<FormProvide>(FormToken)!;
const documentApi = useLetterFileDocumentApi();
const simpleFeatureStore = useSimpleFeatureStore();
const docExpiry = computed(() => simpleFeatureStore.docTrashExpiry);

const paginatedDocs = computed(() => {
  return props.documents.filter(
    (_item, index) => index >= (page.value - 1) * pageSize.value && index < page.value * pageSize.value
  );
});
const page = ref<number>(1);
const pageSize = ref<number>(5);
const total = computed(() => props.documents.length);

function onPaginationChange(pagination: { page: number; pageSize: number }) {
  if (pagination.pageSize !== pageSize.value) {
    page.value = 1;
  } else {
    page.value = pagination.page;
  }
  pageSize.value = pagination.pageSize;
}

function daysLeft(doc: LetterFileDocumentDto) {
  const remaining = docExpiry.value - dayjs().diff(doc.deletedAt!, "days");
  if (remaining > 0) {
    return `${remaining} ${replacePlural(remaining, "jour restant", "jours restants")}`;
  }
  return "dernier jour";
}

function onClose() {
  emit("update:modelValue", false);
}

async function restore(doc: LetterFileDocumentDto) {
  await notifyAsyncOperation(async () => {
    await documentApi.restore(doc.id, letterFile.value!.id);
    await refresh();
    await useAlertManager().createAlert({
      type: AlertType.INFO,
      message: `Le document '${doc.name}${doc.extension}' a été restauré`,
      duration: AlertDuration.MEDIUM,
    });
  }, FormOperation.DOCUMENT);
}

// async function permaDelete(doc: LetterFileDocumentDto) {
//   if (
//     confirm(
//       "Attention cette opération supprimera définitivement le document et toutes ses versions antérieures, êtes vous sûr(e) de vouloir continuer ?"
//     )
//   ) {
//     await notifyAsyncOperation(async () => {
//       await documentApi.delete(doc.id, letterFile.value!.id);
//       await refresh();
//     }, FormOperation.DOCUMENT);
//   }
// }

function getRestoreTooltipMsg(document: LetterFileDocumentDto) {
  if (letterFile.value?.archived) return "Impossible de restaurer un document sur un parapheur archivé";
  if (document.type === LetterFileDocumentType.DEFAULT) {
    return "Restaurer le document et ses versions antérieures";
  }
  return "Restaurer le lien";
}

function formatDate(date: string) {
  return dayjs(date).format("DD/MM/YYYY à HH:mm");
}
</script>
